import { CheckCircle2, Clock, Store, Truck, XCircle } from "lucide-react"

import { FulfillmentType, OrderStatus, type SubOrder } from "@/api/api-client"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"

import type { VendorOrderActionLabels } from "./vendor-order-types"

interface VendorOrderActionsProps {
  subOrder: Pick<SubOrder, "id" | "status" | "fulfillmentType">
  labels?: VendorOrderActionLabels
  shippingInput: string
  isQuoting: boolean
  terminalLabel?: string
  rejectedLabel?: string
  onShippingInputChange: (subOrderId: number, value: string) => void
  onQuoteShipping: (subOrderId: number) => void
  onStatusChange: (subOrderId: number, status: OrderStatus) => void
}

export function VendorOrderActions({
  subOrder,
  labels,
  shippingInput,
  isQuoting,
  terminalLabel,
  rejectedLabel,
  onShippingInputChange,
  onQuoteShipping,
  onStatusChange,
}: VendorOrderActionsProps) {
  const id = subOrder.id
  if (id == null) return null

  const status = subOrder.status as OrderStatus
  const isPickup = subOrder.fulfillmentType === FulfillmentType.Pickup

  if (status === OrderStatus.OrderDeclined) {
    return (
      <div className="flex h-full flex-col items-center justify-center rounded-xl border border-border/50 bg-muted/30 p-6 text-center">
        <XCircle className="mb-3 h-8 w-8 text-destructive opacity-80" />
        <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
          {rejectedLabel}
        </span>
      </div>
    )
  }

  if (status === OrderStatus.OrderComplete) {
    return (
      <div className="flex h-full flex-col items-center justify-center rounded-xl border border-border/50 bg-muted/30 p-6 text-center">
        <CheckCircle2 className="mb-3 h-8 w-8 text-emerald-500 opacity-80" />
        <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
          {terminalLabel}
        </span>
      </div>
    )
  }

  switch (status) {
    case OrderStatus.Pending:
      return (
        <>
          <Button
            onClick={() => onStatusChange(id, OrderStatus.StockVerified)}
            className="h-12 w-full bg-accent font-bold uppercase tracking-widest text-primary-foreground shadow-sm hover:bg-accent/90"
          >
            {labels?.validateStock || "VALIDATE STOCK"}
          </Button>
          <Button
            onClick={() => onStatusChange(id, OrderStatus.OrderDeclined)}
            variant="outline"
            className="h-10 w-full border-destructive text-xs font-bold uppercase tracking-widest text-destructive hover:bg-destructive/10"
          >
            {labels?.rejectOrder || "REJECT ORDER"}
          </Button>
        </>
      )

    case OrderStatus.StockVerified:
      if (isPickup) return null
      return (
        <div className="w-full space-y-3">
          <label
            htmlFor={`shipping-cost-${id}`}
            className="text-xs font-bold uppercase tracking-widest text-muted-foreground"
          >
            {labels?.shippingCostLabel || "Shipping cost (MXN)"}
          </label>
          <Input
            id={`shipping-cost-${id}`}
            type="number"
            min="0"
            step="0.01"
            placeholder="0.00"
            value={shippingInput}
            onChange={(event) => onShippingInputChange(id, event.target.value)}
            className="h-11 font-mono text-sm"
          />
          <Button
            onClick={() => onQuoteShipping(id)}
            disabled={isQuoting}
            className="h-12 w-full bg-accent font-bold uppercase tracking-widest text-primary-foreground hover:bg-accent/90"
          >
            {isQuoting ? "..." : labels?.quoteShipping || "QUOTE SHIPPING"}
          </Button>
        </div>
      )

    case OrderStatus.ShippingQuoted:
      return (
        <div className="flex h-full flex-col items-center justify-center gap-3 rounded-xl border border-violet-100 bg-violet-50 p-6 text-center">
          <Clock className="h-7 w-7 text-violet-400" />
          <span className="text-xs font-bold uppercase tracking-widest text-violet-600">
            {labels?.awaitingPayment || "AWAITING BUYER PAYMENT"}
          </span>
        </div>
      )

    case OrderStatus.PaymentReceived:
      if (isPickup) {
        return (
          <Button
            onClick={() => onStatusChange(id, OrderStatus.ReadyForPickup)}
            className="h-12 w-full bg-cyan-600 font-bold uppercase tracking-widest text-white hover:bg-cyan-700"
          >
            <Store className="mr-2 h-4 w-4" />
            {labels?.markReadyForPickup || "MARK READY FOR PICKUP"}
          </Button>
        )
      }
      return (
        <Button
          onClick={() => onStatusChange(id, OrderStatus.OrderShipped)}
          className="h-12 w-full bg-primary font-bold uppercase tracking-widest text-primary-foreground hover:bg-primary/90"
        >
          <Truck className="mr-2 h-4 w-4" />
          {labels?.shipOrder || "APPROVE & RELEASE"}
        </Button>
      )

    case OrderStatus.OrderShipped:
      return (
        <Button
          onClick={() => onStatusChange(id, OrderStatus.OrderComplete)}
          className="h-12 w-full bg-emerald-600 font-bold uppercase tracking-widest text-white hover:bg-emerald-700"
        >
          {labels?.markComplete || "CONFIRM DELIVERY"}
        </Button>
      )

    case OrderStatus.ReadyForPickup:
      return (
        <Button
          onClick={() => onStatusChange(id, OrderStatus.OrderComplete)}
          className="h-12 w-full bg-emerald-600 font-bold uppercase tracking-widest text-white hover:bg-emerald-700"
        >
          <CheckCircle2 className="mr-2 h-4 w-4" />
          {labels?.confirmPickup || "CONFIRM PICKUP"}
        </Button>
      )

    default:
      return null
  }
}
